/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { RatingStat } from '@coursera/cds-core';
import type { useLocale } from '@coursera/cds-core';

import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';

import _t from 'i18n!nls/product-card';

type Props = { avgProductRating?: number; numProductRatings?: number };
type Locale = ReturnType<typeof useLocale>;

const getRoundedRating = (avgProductRating: number) => {
  return Math.round(avgProductRating * 10) / 10;
};

const getNumberOfReviewsAsString = (locale: Locale, numProductRatings: number) => {
  return new Intl.NumberFormat(locale.locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(numProductRatings);
};

export const getProductCardReviewsAsString = (
  locale: Locale,
  avgProductRating?: number,
  numProductRatings?: number
) => {
  if (!avgProductRating || (numProductRatings && numProductRatings <= 5)) {
    return null;
  }

  if (!numProductRatings) {
    return _t('★ #{productRating}', {
      productRating: getRoundedRating(avgProductRating),
    });
  }

  return _t('★ #{productRating} (#{numRatings})', {
    productRating: getRoundedRating(avgProductRating),
    numRatings: getNumberOfReviewsAsString(locale, numProductRatings),
  });
};

const ProductCardReviews = ({ avgProductRating, numProductRatings }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // WCAG 4.1.2 fix: CDS RatingStat renders role="meter" which is not well-supported
  // by screen readers. Patch it to role="img" with an aria-label so the rating
  // is announced as a single readable value (e.g. "4.7 out of 5 stars").
  useEffect(() => {
    const meterEl = containerRef.current?.querySelector('[role="meter"]');
    if (meterEl) {
      meterEl.setAttribute('role', 'img');
      meterEl.setAttribute('aria-roledescription', 'rating');
      const valueText = meterEl.getAttribute('aria-valuetext');
      if (valueText) {
        meterEl.setAttribute('aria-label', valueText);
      }
      meterEl.removeAttribute('aria-valuemin');
      meterEl.removeAttribute('aria-valuemax');
      meterEl.removeAttribute('aria-valuenow');
      meterEl.removeAttribute('aria-valuetext');
    }
  }, [avgProductRating, numProductRatings]);

  if (!avgProductRating || (numProductRatings && numProductRatings <= 5)) {
    return null;
  }

  const rating = getRoundedRating(avgProductRating);

  const experimentVariant = SiteMiscExperiments.preview('productCardTest1');
  const size = experimentVariant === 'test1' ? 'meta' : 'label';
  return (
    <div ref={containerRef}>
      <RatingStat rating={rating} size={size} reviews={numProductRatings} />
    </div>
  );
};

export default ProductCardReviews;
